export type DonorTier = 'none' | 'tier1' | 'tier2' | 'tier3' | 'tier4' | 'tier5' | 'tier6' | 'tier7' | 'tier8' | 'tier9' | 'tier10'

export interface DonorInfo {
  tier: DonorTier
  name: string
  frameClass: string
  description: string
  minAmount: number
  maxAmount?: number
}

export const DONOR_TIERS: Record<DonorTier, DonorInfo> = {
  none: {
    tier: 'none',
    name: 'No Frame',
    frameClass: '',
    description: '',
    minAmount: 0
  },
  tier1: {
    tier: 'tier1',
    name: 'Neophyte',
    frameClass: 'frame-neophyte',
    description: 'Entered the outer hall',
    minAmount: 1,
    maxAmount: 99.99
  },
  tier2: {
    tier: 'tier2',
    name: 'Patron',
    frameClass: 'frame-patron',
    description: 'Backs the work',
    minAmount: 100,
    maxAmount: 499.99
  },
  tier3: {
    tier: 'tier3',
    name: 'Steward',
    frameClass: 'frame-steward',
    description: 'Keeps the books',
    minAmount: 500,
    maxAmount: 999.99
  },
  tier4: {
    tier: 'tier4',
    name: 'Factor',
    frameClass: 'frame-factor',
    description: 'Executes trade and trust',
    minAmount: 1000,
    maxAmount: 2999.99
  },
  tier5: {
    tier: 'tier5',
    name: 'Quaestor',
    frameClass: 'frame-quaestor',
    description: 'Keys to the treasury',
    minAmount: 3000,
    maxAmount: 4999.99
  },
  tier6: {
    tier: 'tier6',
    name: 'Warden',
    frameClass: 'frame-warden',
    description: 'Guards the vault',
    minAmount: 5000,
    maxAmount: 9999.99
  },
  tier7: {
    tier: 'tier7',
    name: 'Magnate',
    frameClass: 'frame-magnate',
    description: 'Bends markets',
    minAmount: 10000,
    maxAmount: 24999.99
  },
  tier8: {
    tier: 'tier8',
    name: 'Archon',
    frameClass: 'frame-archon',
    description: 'Rules a province',
    minAmount: 25000,
    maxAmount: 49999.99
  },
  tier9: {
    tier: 'tier9',
    name: 'Exarch',
    frameClass: 'frame-exarch',
    description: 'Governs dominions',
    minAmount: 50000,
    maxAmount: 99999.99
  },
  tier10: {
    tier: 'tier10',
    name: 'Sovereign',
    frameClass: 'frame-sovereign',
    description: 'Crown of the House',
    minAmount: 100000
  }
}

export function getDonorTier(totalDonated: number): DonorTier {
  if (totalDonated >= DONOR_TIERS.tier10.minAmount) return 'tier10' // Sovereign - 100k+
  if (totalDonated >= DONOR_TIERS.tier9.minAmount) return 'tier9' // Exarch - 50k-99k
  if (totalDonated >= DONOR_TIERS.tier8.minAmount) return 'tier8' // Archon - 25k-49k
  if (totalDonated >= DONOR_TIERS.tier7.minAmount) return 'tier7' // Magnate - 10k-24k
  if (totalDonated >= DONOR_TIERS.tier6.minAmount) return 'tier6' // Warden - 5k-9k
  if (totalDonated >= DONOR_TIERS.tier5.minAmount) return 'tier5' // Quaestor - 3k-4k
  if (totalDonated >= DONOR_TIERS.tier4.minAmount) return 'tier4' // Factor - 1k-2k
  if (totalDonated >= DONOR_TIERS.tier3.minAmount) return 'tier3' // Steward - 500-999
  if (totalDonated >= DONOR_TIERS.tier2.minAmount) return 'tier2' // Patron - 100-499
  if (totalDonated >= DONOR_TIERS.tier1.minAmount) return 'tier1' // Neophyte - 1-99
  return 'none'
}

export function getDonorInfo(tier: DonorTier): DonorInfo {
  return DONOR_TIERS[tier] || DONOR_TIERS.none
}

/**
 * ⭐ THE ONE PLACE THAT DECIDES WHICH FRAME A USER GETS (owner 2026-08-16: "set the tier_override
 * and switch the check to read it").
 *
 * Before this, the rule was a hardcoded `username === 'TheDesperateTrader' ? 'tier10' : ...`, copied
 * into Navbar.tsx, UserAvatar.tsx, SupportPostCard.tsx and MessageBubble.tsx. Four copies of one
 * rule, each of which a rename would have silently broken, and none of which could grant a tier to
 * anybody else without a fifth copy.
 *
 * PRECEDENCE IS override > earned > none, and the ORDER IS THE POINT. An override is a manual grant
 * and must beat the money-derived tier, or granting one to an existing donor would DOWNGRADE them.
 * Falling through to the earned tier means clearing an override restores what they actually paid
 * for, which is the correct behaviour for a rank that is supposed to track spend.
 *
 * ⚠ A CALLER THAT CANNOT SUPPLY tierOverride GETS THE EARNED TIER, which is the right answer for the
 * 999 of 1,000 accounts where the column is NULL. So adding the column to a query is an improvement,
 * never a prerequisite.
 */
export function resolveDisplayTier(
  donorTier?: string | null,
  tierOverride?: string | null,
): DonorTier {
  const granted = tierOverride && tierOverride !== 'none' ? tierOverride : null
  const earned = donorTier && donorTier !== 'none' ? donorTier : null
  const chosen = granted ?? earned ?? 'none'
  return (chosen in DONOR_TIERS ? chosen : 'none') as DonorTier
}

export function getDonorFrameInfo(tier: DonorTier) {
  const info = getDonorInfo(tier)
  if (tier === 'none') return null
  
  return {
    name: info.name,
    frameClass: info.frameClass,
    description: info.description,
    tier: info.tier
  }
}

/** Tailwind classes + icon glyph for compact donor tier badges (see DonorBadge.tsx). */
// Hue-preserving light/dark pairing: a light tint of the SAME hue carries dark -800 text in light
// mode (COLOUR-100 -> COLOUR-800 keeps tier identity readable AS identity), with the original dark
// badge restored behind dark:. Measured 2026-11-01 (UI/Visual Polish window): the old light-only form
// put text-<hue>-100 on bg-<hue>-950/40, which composites to ~rgb(180,180,180) on a white light-mode
// ground (~1.86:1, fails AA). See findings donor-tier-badges-unreadable-in-light-1101.
const DONOR_BADGE_BY_TIER: Record<Exclude<DonorTier, 'none'>, { color: string; icon: string }> = {
  tier1: { color: 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100', icon: '◇' },
  tier2: { color: 'border-zinc-300 bg-zinc-100 text-zinc-800 dark:border-zinc-400/40 dark:bg-zinc-800/50 dark:text-zinc-100', icon: '◇' },
  tier3: { color: 'border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-950/40 dark:text-sky-100', icon: '◆' },
  tier4: { color: 'border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100', icon: '◆' },
  tier5: { color: 'border-cyan-300 bg-cyan-100 text-cyan-800 dark:border-cyan-500/40 dark:bg-cyan-950/40 dark:text-cyan-100', icon: '◆' },
  tier6: { color: 'border-violet-300 bg-violet-100 text-violet-800 dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-100', icon: '◆' },
  tier7: { color: 'border-orange-300 bg-orange-100 text-orange-800 dark:border-orange-500/40 dark:bg-orange-950/40 dark:text-orange-100', icon: '◆' },
  tier8: { color: 'border-yellow-300 bg-yellow-100 text-yellow-800 dark:border-yellow-500/40 dark:bg-yellow-950/40 dark:text-yellow-100', icon: '◆' },
  tier9: { color: 'border-pink-300 bg-pink-100 text-pink-800 dark:border-pink-500/40 dark:bg-pink-950/40 dark:text-pink-100', icon: '◆' },
  tier10: { color: 'border-red-400 bg-red-100 text-red-800 dark:border-red-500/50 dark:bg-red-950/50 dark:text-red-100', icon: '★' },
}

export type DonorBadgeInfo = {
  color: string
  icon: string
  badge: string
}

export function getDonorBadgeComponent(tier: string): DonorBadgeInfo | null {
  const key = String(tier ?? 'none').toLowerCase() as DonorTier
  if (key === 'none') return null
  const style = DONOR_BADGE_BY_TIER[key as Exclude<DonorTier, 'none'>]
  if (!style) return null
  const info = getDonorInfo(key)
  return { color: style.color, icon: style.icon, badge: info.name }
}

